import { useEffect, useInsertionEffect } from "react"
import { Outlet } from "react-router"
import { useTranslation } from "react-i18next"
import { HelmetProvider, Helmet } from "react-helmet-async"
import Clarity from "@microsoft/clarity"

import {
  useGetConfigQuery,
  useGetTranslationsQuery,
} from "./apiSlice"

export const App = () => {
  const { i18n } = useTranslation("gift_card")

  const { data: configData } = useGetConfigQuery()

  const language = configData?.locales?.find((locale: any) => locale.code === i18n.language) ? i18n.language : configData?.locales?.[0]?.code

  const {
    data: commonTranslationsData,
  } = useGetTranslationsQuery({ language, namespace: "common" }, { skip: !language })

  const {
    data: translationsData,
  } = useGetTranslationsQuery({ language, namespace: "gift_card" }, { skip: !language })

  useEffect(() => {
    if (!language || !commonTranslationsData || !translationsData) return;
    i18n.addResourceBundle(language, "common", commonTranslationsData || {})
    i18n.addResourceBundle(language, "gift_card", translationsData || {})
    i18n.changeLanguage(language)
  }, [ language, commonTranslationsData, translationsData, i18n ])
  
  useInsertionEffect(() => {
    if (configData) {
      const manifest = {
        "name": configData?.title,
        "short_name": configData?.title,
        "icons": Object.entries(configData?.favicons || {})
          .map(([ key, value ]) => ({ src: value, sizes: key, type: "image/png" })),
        "theme_color": "#0D0D0D",
        "background_color": "#FEFEFE",
        "display": "standalone",
        "start_url": window.location.protocol + window.location.host,
      }
      const link = document.createElement("link")
      link.setAttribute("rel", "manifest")
      link.setAttribute("href", URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: "application/json" })))
      document.head.appendChild(link)
    }
  }, [ configData ])

  useInsertionEffect(() => {
    let google_tag_manager_project_id = ""

    try {
      const google_tag_manager_project_id_parsed = JSON.parse(import.meta.env.VITE_GOOGLE_TAG_MANAGER_PROJECT_ID || "{}")
      google_tag_manager_project_id = google_tag_manager_project_id_parsed[window.location.host]
    } catch (error: any) {
      console.error(error)
    }

    if (google_tag_manager_project_id) {
      const script1 = document.createElement("script")
      script1.setAttribute("id", "google-tag-manager-script-1")
      script1.setAttribute("async", "async")
      script1.setAttribute("src", `https://www.googletagmanager.com/gtag/js?id=${google_tag_manager_project_id}`)
      const script2 = document.createElement("script");
      script2.setAttribute("id", "google-tag-manager-script-2");
      script2.innerHTML = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${google_tag_manager_project_id}');`
      document.head.prepend(script1, script2)

      return () => {
        const script1 = document.getElementById("google-tag-manager-script-1")
        script1?.remove()
        const script2 = document.getElementById("google-tag-manager-script-2")
        script2?.remove()
      }
    }
  }, [])

  useEffect(() => {
    let clarity_project_id_by_host = ""

    try {
        const clarity_project_id_by_host_parsed = JSON.parse(import.meta.env.VITE_CLARITY_PROJECT_ID || "{}")
        clarity_project_id_by_host = clarity_project_id_by_host_parsed[window.location.host]
    } catch (error: any) {
        console.error(error)
    }

    if (clarity_project_id_by_host) {
        Clarity.init(clarity_project_id_by_host)
    }
  }, [])

  if (!configData || !commonTranslationsData || !translationsData) {
    return null
  }

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{configData?.title}</title>
          {
            Object.entries(configData?.favicons || {})
              .map(([ key, value ]: any[]) => (
                  <link
                    key={key}
                    rel="icon"
                    type="image/png"
                    sizes={key}
                    href={`${value}?${configData?.title}`}
                  />
              ))
          }
        </Helmet>
      </HelmetProvider>
      <Outlet />
    </>
  )
}
